import { sort } from "@prismicio/editor-support/Array"
import { getRequestCredentials, request } from "@prismicio/editor-support/Http"
import {
  revalidateData,
  updateFetcherData,
  useRequest,
} from "@prismicio/editor-support/Suspense"
import { z } from "zod"

import {
  type AuthStrategy,
  throwConfigError,
  useEditorConfig,
} from "../../../EditorConfig"
import { getRepository, useProfile } from "../../service"

export interface UploaderWithMeta {
  id: string
  name: string
  firstName?: string
  lastName?: string
  email?: string
  uploadedAvatar?: string
}

export function useUploaders() {
  const config = useEditorConfig()

  if (!config.mediaLibrary) throwConfigError("mediaLibrary")

  const { assetsApiBaseUrl, coreApiBaseUrl, authStrategy, repository } = config

  const { authors } = useRequest(getRepository, [coreApiBaseUrl, authStrategy])

  const { items: uploaders } = useRequest(getUploaders, [
    assetsApiBaseUrl,
    repository,
    authStrategy,
  ])

  const { shortId } = useProfile()

  const authorsById = new Map(authors.map((author) => [author.id, author]))

  const uploadersWithMeta: UploaderWithMeta[] = []
  for (const uploader of uploaders) {
    if (uploader.id === "ghost") {
      uploadersWithMeta.push({
        id: uploader.id,
        name: "Ghost",
        uploadedAvatar:
          "https://images.prismic.io/page-builder-assets/ZuAXWBoQrfVKl5sD_ghost_user.png?auto=format,compress&w=64",
      })
      continue
    }

    const author = authorsById.get(uploader.id)
    if (!author) continue

    let uploadedAvatar
    if (author.uploadedAvatar) {
      const url = new URL(author.uploadedAvatar)
      url.searchParams.set("w", "64")
      uploadedAvatar = url.toString()
    }

    uploadersWithMeta.push({
      id: uploader.id,
      name: [author.firstname, author.lastname].filter(Boolean).join(" "),
      firstName: author.firstname,
      lastName: author.lastname,
      email: author.email,
      uploadedAvatar,
    })
  }

  const uploadersById = new Map(
    uploadersWithMeta.map((uploader) => [uploader.id, uploader]),
  )

  return {
    uploaders: sort(
      uploadersWithMeta,
      // Current user should always be at the top
      (uploader) => (uploader.id === shortId ? 0 : 1),
      (user) => user.name,
    ),
    uploadersById,
  }
}

function getUploaders(
  baseUrl: URL,
  repository: string,
  authStrategy: AuthStrategy,
): Promise<UploadersResponse> {
  return request(new URL("uploaders", baseUrl), {
    credentials: getRequestCredentials(authStrategy),
    okType: uploadersResponse,
    headers: {
      repository,
    },
  })
}

export const uploaderSchema = z.object({
  id: z.string(),
})

const uploadersResponse = z.object({
  items: z.array(uploaderSchema),
})

export type Uploader = z.TypeOf<typeof uploaderSchema>
type UploadersResponse = z.TypeOf<typeof uploadersResponse>

type UpdateUploadersFunction = (
  currentItems: Uploader[],
) => Uploader[] | undefined

export function updateUploadersCache(updateFunction: UpdateUploadersFunction) {
  updateFetcherData(getUploaders, (body) => {
    const { items } = body

    const newItems = updateFunction(items)
    if (!newItems) return undefined

    return {
      items: newItems,
    }
  })
}

export function useRevalidateUploaders() {
  const config = useEditorConfig()

  if (!config.mediaLibrary) throwConfigError("mediaLibrary")

  const { assetsApiBaseUrl, authStrategy, repository } = config

  return () => {
    void revalidateData(getUploaders, [
      assetsApiBaseUrl,
      repository,
      authStrategy,
    ])
  }
}
